import type { ComponentType } from 'react';

import AppLauncherSettingsForm from '@/features/widgets/app-launcher/AppLauncherSettingsForm';
import AppLauncherWidget from '@/features/widgets/app-launcher/AppLauncherWidget';
import { DEFAULT_APP_LAUNCHER_CONFIG } from '@/features/widgets/app-launcher/config';
import ElectricityWidget from '@/features/widgets/electricity/ElectricityWidget';
import { DEFAULT_HSL_BUSES_CONFIG } from '@/features/widgets/hsl-buses/config';
import HslBusesSettingsForm from '@/features/widgets/hsl-buses/HslBusesSettingsForm';
import HslBusesWidget from '@/features/widgets/hsl-buses/HslBusesWidget';
import { DEFAULT_SAUNA_WIDGET_CONFIG } from '@/features/widgets/sauna/config';
import SaunaSettingsForm from '@/features/widgets/sauna/SaunaSettingsForm';
import SaunaWidget from '@/features/widgets/sauna/SaunaWidget';
import { DEFAULT_SENSORS_CONFIG } from '@/features/widgets/sensors/config';
import SensorsSettingsForm from '@/features/widgets/sensors/SensorsSettingsForm';
import SensorsWidget from '@/features/widgets/sensors/SensorsWidget';
import { DEFAULT_STOCKS_CONFIG } from '@/features/widgets/stocks/config';
import StocksSettingsForm from '@/features/widgets/stocks/StocksSettingsForm';
import StocksWidget from '@/features/widgets/stocks/StocksWidget';
import WeatherWidget from '@/features/widgets/weather/WeatherWidget';

/**
 * Widget identifiers. The dashboard layout is fixed and code-defined
 * (see DashboardGrid); the registry only records which widget types exist
 * plus their default config + settings form. Doc id in Firestore = widget id.
 */
export type WidgetId = 'hsl-buses' | 'weather' | 'electricity' | 'sensors' | 'sauna' | 'stocks' | 'app-launcher';

export interface WidgetSettingsFormProps {
  widgetId: WidgetId;
  config: Record<string, unknown>;
  onSaved: () => void;
  formId: string;
}

export interface RegisteredWidget<C = Record<string, unknown>> {
  id: WidgetId;
  title: string;
  component: ComponentType;
  defaultConfig: C;
  configurable: boolean;
  SettingsForm?: ComponentType<WidgetSettingsFormProps>;
}

export const widgetRegistry: Record<WidgetId, RegisteredWidget> = {
  'hsl-buses': {
    id: 'hsl-buses',
    title: 'Next buses',
    component: HslBusesWidget,
    defaultConfig: DEFAULT_HSL_BUSES_CONFIG as unknown as Record<string, unknown>,
    configurable: true,
    SettingsForm: HslBusesSettingsForm,
  },
  weather: {
    id: 'weather',
    title: 'Weather',
    component: WeatherWidget,
    defaultConfig: {},
    configurable: false,
  },
  electricity: {
    id: 'electricity',
    title: 'Electricity',
    component: ElectricityWidget,
    defaultConfig: {},
    configurable: false,
  },
  sensors: {
    id: 'sensors',
    title: 'Sensors',
    component: SensorsWidget,
    defaultConfig: DEFAULT_SENSORS_CONFIG as unknown as Record<string, unknown>,
    configurable: true,
    SettingsForm: SensorsSettingsForm,
  },
  sauna: {
    id: 'sauna',
    title: 'Sauna',
    component: SaunaWidget,
    defaultConfig: DEFAULT_SAUNA_WIDGET_CONFIG as unknown as Record<string, unknown>,
    configurable: true,
    SettingsForm: SaunaSettingsForm,
  },
  stocks: {
    id: 'stocks',
    title: 'Stocks',
    component: StocksWidget,
    defaultConfig: DEFAULT_STOCKS_CONFIG as unknown as Record<string, unknown>,
    configurable: true,
    SettingsForm: StocksSettingsForm,
  },
  'app-launcher': {
    id: 'app-launcher',
    title: 'My Apps',
    component: AppLauncherWidget,
    defaultConfig: DEFAULT_APP_LAUNCHER_CONFIG as unknown as Record<string, unknown>,
    configurable: true,
    SettingsForm: AppLauncherSettingsForm,
  },
};
